import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSlideData } from '@context/slide-data';
import { usePresentation } from '@context/presentation';
import { convertToNumber, formatValue, validateInput, formatInput } from '@utils/validateInputs';
import { setLoanInput, setLoanCalculations } from '@actions/creators';
import { FORMAT_TYPES, BROWSER_STORAGE_KEYS } from '@constants';
import { updateLoanInputs } from '@services/sse';
import { LoanCalculatorStyle, LoanCalculatorInputs, LoanCalculatorTotals, LoanCalculatorInput } from './styles';
import { getBrowserStorageItem, updateSavedLoanCalculations } from '@utils/browserStorage';

export const LoanCalculator = () => {
    const slideKey = 'loan_calculator';

    const { t } = useTranslation();
    const { clientViewing } = usePresentation();
    const { slideState, dispatch, data, desktop } = useSlideData();
    const { loanCalculations } = slideState;
    const { proposedTerms } = data.details;
    const { dollars } = FORMAT_TYPES;
    
    const handleChange = (event, inputName) => {
        const { name, value } = event.target;

        const formattedValue = formatInput(value, inputName);

        dispatch(setLoanInput(name, formattedValue));
        updateLoanInputs({ name, value: formattedValue });
    };

    const estimateTotalSavings = (newRepayment, oldRepayment) => {
        const rawTotalSavings = convertToNumber(newRepayment) - convertToNumber(oldRepayment);

        const estTotalSavings = formatValue(rawTotalSavings, dollars.noDecimal);
        const totalRepayment = formatValue(newRepayment, dollars.noDecimal).slice(1);

        // To-do: something less repetitive
        dispatch(setLoanInput('estTotalSavings', estTotalSavings));
        dispatch(setLoanInput('totalRepayment', totalRepayment));
        updateLoanInputs({ name: 'estTotalSavings', value: estTotalSavings });
        updateLoanInputs({ name: 'totalRepayment', value: totalRepayment });
        updateSavedLoanCalculations('estTotalSavings', estTotalSavings);
        updateSavedLoanCalculations('totalRepayment', totalRepayment);
    };

    const calculateTotalRepayment = () => {
        const { monthlyPayment, loanTerm } = loanCalculations;
        const newTotalRepayment = convertToNumber(monthlyPayment) * convertToNumber(loanTerm);
      
        estimateTotalSavings(newTotalRepayment, proposedTerms.totalRepayment);
    };

    const handleBlur = (name) => {
        const newValue = loanCalculations[name];

        updateSavedLoanCalculations(name, newValue);
        calculateTotalRepayment();
    };

    const Input = ({ name, type = 'text' }) => (
        clientViewing ? 
            <div aria-labelledby={name}>
                {loanCalculations[name]}
            </div> : <LoanCalculatorInput 
                        type={type}
                        id={`input-${name}`}
                        aria-labelledby={name}
                        value={loanCalculations[name]}
                        name={name}
                        onBlur={() => handleBlur(name)}
                        onKeyDown={(evt) => validateInput(evt, name)}
                        onChange={event => handleChange(event, name)} />
    );

    useEffect(() => {
        const { loanCalculationsKey } = BROWSER_STORAGE_KEYS;
        const savedCalculations = getBrowserStorageItem(loanCalculationsKey);

        if (savedCalculations) {
            dispatch(setLoanCalculations({...loanCalculations, ...savedCalculations}));
        }
    }, []);

    return (
        <LoanCalculatorStyle desktop={desktop}>
           <LoanCalculatorInputs>
                <div>
                    <label htmlFor='input-loanAmount' id='loanAmount'>
                        {t(`${slideKey}.loan_amount`)}
                    </label>
                    <span>
                        <strong>$</strong>
                        {Input({ name: 'loanAmount' })}
                    </span>
                </div>
                <div>
                    <label htmlFor='input-APR' id='APR'>
                        {t(`${slideKey}.APR`)}
                    </label>
                    <span>
                        <strong>%</strong>
                        {Input({ name: 'APR'})}
                    </span>
                </div>
                <div>
                    <label id='totalRepayment'>
                        {t(`${slideKey}.repayment`)}
                    </label>
                    <span style={{ display: 'inherit', alignItems: 'center'}}>
                        <strong>$</strong>
                        <div aria-labelledby='totalRepayment'>
                            {loanCalculations.totalRepayment}
                        </div>
                    </span>
                </div>
                <div>
                    <label htmlFor='input-loanTerm' id='loanTerm'>
                        {t(`${slideKey}.loan_term`)}
                    </label>
                    <span>
                        <strong>
                            <FontAwesomeIcon icon={faCalendar}/>
                        </strong>
                        {Input({ name: 'loanTerm' })}
                    </span>
                </div>
                <div>
                    <label htmlFor='input-monthlyPayment' id='monthlyPayment'>
                        {t(`${slideKey}.monthly_payment`)}
                    </label>
                    <span>
                        <strong>$</strong>
                        {Input({ name: 'monthlyPayment' })}
                    </span>
                </div>
           </LoanCalculatorInputs>
           <LoanCalculatorTotals>  
                <h2><span>{t(`${slideKey}.proposed_terms`)}</span></h2>
                <div>
                    <span>{t(`${slideKey}.repayment`)}</span>
                    <span id='first-row'>
                        <strong>$</strong>
                        {proposedTerms.totalRepayment.slice(1)}
                    </span>
                </div>
                <div>
                    <span>{t(`${slideKey}.ccm_term`)}</span>
                    <span>
                        <FontAwesomeIcon icon={faCalendar}/>
                        {proposedTerms.loanTerm}
                    </span>
                </div>
                <div>
                    <span>{t(`${slideKey}.monthly_payment`)}</span>
                    <span>
                        <strong>$</strong>
                        {proposedTerms.monthlyPayment.slice(1)}
                    </span>
                </div>
                <div>
                    <span>{t(`${slideKey}.monthly_savings`)}</span>
                    <span>
                        <strong>$</strong>
                        {proposedTerms.monthlySavings.slice(1)}
                    </span>
                </div>
                <div>
                    <span>{t(`${slideKey}.est_total_savings`)}</span>
                    <span id='last-row'>
                        <strong>$</strong>
                        {
                            convertToNumber(loanCalculations.estTotalSavings) > 0 ? loanCalculations.estTotalSavings.toString().slice(1) : '--'
                        }
                    </span>
                </div>
           </LoanCalculatorTotals>
        </LoanCalculatorStyle>
    );
};

export default LoanCalculator;
